'use client'

import Link from 'next/link'
import { Mail, Phone, MapPin, Globe, ArrowRight, Shield } from 'lucide-react'
import footerData from '@/data/footer.json'
import { WhatsAppIcon } from './WhatsAppIcon'

function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  )
}

const socialIconMap: Record<string, React.FC<{ className?: string }>> = {
  Linkedin: LinkedinIcon,
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  MessageCircle: WhatsAppIcon,
}

export function Footer() {
  return (
    <footer style={{ background: '#0F172A' }} className="text-white">

      {/* CTA Banner */}
      <div
        className="border-b"
        style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)', borderColor: '#1E40AF' }}
      >
        <div className="container-narrow py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-1">
                Ready to Get Started?
              </h3>
              <p className="text-blue-200 text-sm">
                Contact us today for a free consultation — no obligations.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#B45309] text-white rounded-xl font-bold hover:bg-[#92400E] transition-all duration-300 text-sm"
              >
                Book Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/971586155899"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 text-white rounded-xl font-bold hover:bg-white/25 transition-all duration-300 border border-white/25 text-sm"
              >
                <WhatsAppIcon className="w-4 h-4 fill-current" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-narrow py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">

          {/* Company Info - spans 2 cols on lg */}
          <div className="lg:col-span-2">
            <div className="text-2xl font-bold text-white mb-1">
              {footerData.footer.company.name}
            </div>
            <div className="text-sm text-[#FBBF24] font-semibold mb-4">
              Document Clearing Solutions
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">
              {footerData.footer.company.description}
            </p>

            {/* Contact Quick Links */}
            <div className="space-y-2 mb-5">
              <a
                href="tel:+971586155899"
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-200 text-sm"
              >
                <Phone className="w-4 h-4 text-[#1D4ED8]" />
                +971 58 615 5899
              </a>
              <a
                href="mailto:alishratdocumentsclearingservi@gmail.com"
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors duration-200 text-sm"
              >
                <Mail className="w-4 h-4 text-[#D97706]" />
                alishratdocumentsclearingservi@gmail.com
              </a>
              <div className="flex items-center gap-2 text-slate-300 text-sm">
                <MapPin className="w-4 h-4 text-[#059669]" />
                Dubai, United Arab Emirates
              </div>
            </div>

            {/* Social Icons */}
            <div className="flex gap-2">
              {footerData.footer.social.map((social) => {
                const Icon = socialIconMap[social.icon]
                return (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 hover:-translate-y-0.5"
                    style={{ background: '#1E293B', color: '#94A3B8' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#1D4ED8'
                      e.currentTarget.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#1E293B'
                      e.currentTarget.style.color = '#94A3B8'
                    }}
                    aria-label={social.name}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                  </a>
                )
              })}
            </div>
          </div>

          {/* Footer Links Sections */}
          {/* Services Column */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">
              Services
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Golden Visa', href: '/golden-visa' },
                { label: 'Power of Attorney', href: '/power-of-attorney' },
                { label: 'Business POA', href: '/business-power-of-attorney' },
                { label: 'Car / Vehicle POA', href: '/car-power-of-attorney' },
                { label: 'Property Conveyancing', href: '/property-conveyancing' },
                { label: 'Document Attestation', href: '/document-services' },
                { label: 'True Copy Certification', href: '/true-copy-attestation' },
                { label: 'POA Notarization', href: '/poa-services' },
              ].map((link, idx) => (
                <li key={idx}>
                  <Link
                    href={link.href}
                    className="text-slate-400 hover:text-white text-sm transition-colors duration-200 flex items-center gap-1.5 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {footerData.footer.sections.map((section) => (
            <div key={section.id}>
              <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">
                {section.title}
              </h4>
              <ul className="space-y-2.5">
                {section.links.map((link, idx) => (
                  <li key={idx}>
                    <a
                      href={link.href}
                      className="text-slate-400 hover:text-white text-sm transition-colors duration-200 flex items-center gap-1.5 group"
                    >
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div
          className="border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
          style={{ borderColor: '#1E293B' }}
        >
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Shield className="w-4 h-4 text-[#1D4ED8]" />
            <span>{footerData.footer.copyright}</span>
          </div>
          <div className="flex gap-6">
            {footerData.footer.legal.map((link, idx) => (
              <Link
                key={idx}
                href={link.href}
                className="text-slate-400 hover:text-white text-sm transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
